import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRBAC } from '../../contexts/RBACContext';
import { AccessDenied } from './AccessDenied';

interface ProtectedRouteProps {
  children: ReactNode;
  permissions?: { resource: string; action: string; scope?: string }[];
  roles?: string[];
  requireAll?: boolean;
}

/**
 * ProtectedRoute component that handles route-level permission checking.
 * Uses RBAC v2 permission-based checks. Prefer permissions over roles.
 *
 * On unauthorized access, renders an in-place AccessDenied message
 * (preserves the URL so the user knows which page they tried to reach).
 *
 * Usage:
 * <ProtectedRoute permissions={[{ resource: "projects", action: "view" }]}>
 *   <ProjectsPage />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permissions,
  roles,
  requireAll = false,
}) => {
  const { user, hasRole, hasAnyPermission, hasAllPermissions, isLoading } = useRBAC();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (roles && roles.length > 0) {
    const hasRoleAccess = requireAll
      ? roles.every(role => hasRole(role))
      : roles.some(role => hasRole(role));

    if (!hasRoleAccess) {
      return <AccessDenied requiredRoles={roles} />;
    }
  }

  if (permissions && permissions.length > 0) {
    const hasPermissionAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasPermissionAccess) {
      return <AccessDenied requiredPermissions={permissions} />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
